import * as $ from "jquery";
import {Map, Overlay, View} from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import {Style, Stroke, Fill} from "ol/style";
import srbijaGeoJson from "./../srbija.geo.json";
import * as BS from "bootstrap";
import {Server} from "./Server";
import {MapEvent, MapPoint, TimeFilter} from "./Types";
import {Format} from "./Format";
import {ListPanel} from "./ListPanel";

export type MapOptions = {
    time: TimeFilter, area: string, atDate: string | null, showPoints: boolean
}

const SERBIA_CENTER = [20.70, 44.20];
const SERBIA_ZOOM = 7.3;
/** Оквир земље – мапа се уклапа у њега, па изглед не зависи од ширине прозора. */
const SERBIA_EXTENT: [number, number, number, number] = [18.82, 41.85, 23.01, 46.19];

export class MapView {
    public static mapOptions: MapOptions = {time: 'TODAY', area: '', atDate: null, showPoints: false};
    public static map: Map;
    public static events: MapEvent[] = [];
    public static points: MapPoint[] = [];

    private static eventMarkers: Overlay[] = [];
    private static pointMarkers: Overlay[] = [];
    private static modal: BS.Modal;

    public static setup(): void {
        MapView.map = new Map({
            target: 'map',
            layers: [
                new TileLayer({source: new OSM()}),
                MapView.serbiaOutlineLayer()
            ],
            view: new View({center: SERBIA_CENTER, zoom: SERBIA_ZOOM}),
        });
        MapView.resetView();
        MapView.modal = new BS.Modal(document.getElementById('eventModal'), {backdrop: true});
        window['mmap'] = MapView.map;   // za dijagnostiku iz konzole
        // odbori se učitavaju jednom
        $.get(Server.endpoint + '/events/data/points').then((points: MapPoint[]) => {
            MapView.points = points;
            MapView.renderPoints();
        });
    }

    /** Podebljana kontura granica Republike Srbije (uključujući Kosovo i Metohiju). */
    private static serbiaOutlineLayer(): VectorLayer<VectorSource> {
        return new VectorLayer({
            source: new VectorSource({
                features: new GeoJSON().readFeatures(srbijaGeoJson)
            }),
            style: new Style({
                stroke: new Stroke({color: '#14213d', width: 3}),
                fill: new Fill({color: 'rgba(29, 63, 143, 0.06)'})
            }),
            zIndex: 1,
            // Kontura je uopstena (Natural Earth 10m) i sluzi za pregled cele zemlje.
            // Na blizim zumovima se sakriva jer OSM podloga vec crta tacnu granicu.
            maxZoom: 9.5
        });
    }

    public static resetView(): void {
        MapView.map.getView().fit(SERBIA_EXTENT, {padding: [24, 24, 24, 24], duration: 300});
    }

    public static focus(lat: number, lon: number, zoom = 15): void {
        MapView.map.getView().animate({center: [lon, lat], zoom: zoom, duration: 400});
    }

    public static reloadMapData(): void {
        const params: any = {time: MapView.mapOptions.time, area: MapView.mapOptions.area};
        if (MapView.mapOptions.time === 'AT_DATE' && MapView.mapOptions.atDate) {
            params.atDate = MapView.mapOptions.atDate;
        }
        $.get(Server.endpoint + '/events/data/map', params).then((events: MapEvent[]) => {
            MapView.events = events;
            MapView.renderEvents();
            MapView.renderPoints();
            ListPanel.render(events);
        });
    }

    private static clear(markers: Overlay[]): void {
        markers.forEach(m => MapView.map.removeOverlay(m));
    }

    private static makeMarker(cls: string, icon: string, color: string, lat: number, lon: number, title: string, onClick: () => void): Overlay {
        const el = $('#map-marker-template').clone(true).removeAttr('id').addClass(cls).show();
        el.attr('title', title).css('color', color);
        el.find('.marker-icon').addClass(icon);
        el.on('click', onClick);
        const overlay = new Overlay({element: el[0], positioning: 'bottom-center', stopEvent: true});
        overlay.setPosition([lon, lat]);
        MapView.map.addOverlay(overlay);
        return overlay;
    }

    /**
     * Један маркер по термину. Термин који се понавља стиже више пута (по датуму),
     * па се на мапи приказује најближе понављање, а списак приказује све датуме.
     */
    private static renderEvents(): void {
        MapView.clear(MapView.eventMarkers);
        MapView.eventMarkers = [];
        const isToday = MapView.mapOptions.time === 'TODAY';
        const seen = new Set<string>();
        MapView.events.forEach(ev => {
            if (seen.has(ev.uuid)) return;
            seen.add(ev.uuid);
            let cls = 'marker-event';
            if (isToday && Format.endedToday(ev)) cls += ' marker-ended';
            const m = MapView.makeMarker(cls, 'fa-location-dot', ev.color, ev.latitude, ev.longitude,
                `${ev.title} · ${Format.timeRange(ev)}`, () => MapView.showEvent(ev));
            MapView.eventMarkers.push(m);
        });
    }

    /**
     * Odbori bez aktivnog termina u trenutnom prikazu – sive ikone.
     * Vise odbora na istoj adresi (npr. niske gradske opstine) daje jedan marker.
     */
    private static renderPoints(): void {
        MapView.clear(MapView.pointMarkers);
        MapView.pointMarkers = [];
        if (!MapView.mapOptions.showPoints) return;
        const withEvent = new Set(MapView.events.map(e => e.pointUuid).filter(x => !!x));
        const groups: Record<string, MapPoint[]> = {};
        MapView.points.forEach(p => {
            if (withEvent.has(p.uuid)) return;
            if (MapView.mapOptions.area && p.areaCode !== MapView.mapOptions.area) return;
            const key = p.latitude.toFixed(5) + ',' + p.longitude.toFixed(5);
            (groups[key] = groups[key] || []).push(p);
        });
        Object.keys(groups).forEach(key => {
            const list = groups[key];
            const title = list.length === 1 ? list[0].name : list[0].municipality + ' – ' + list.length + ' одбора';
            // одбор са просторијама: пуна ознака; без просторија: шупља
            const premises = list.some(p => p.premises);
            const cls = premises ? 'marker-point marker-premises' : 'marker-point marker-nopremises';
            const icon = premises ? 'fa-house-flag' : 'fa-circle-dot';
            const color = premises ? '#1d4ed8' : '#9aa0a6';
            const m = MapView.makeMarker(cls, icon, color, list[0].latitude, list[0].longitude,
                title + (premises ? '' : ' (нема просторије)'), () => MapView.showPoint(list));
            MapView.pointMarkers.push(m);
        });
    }

    public static showEvent(ev: MapEvent): void {
        const el = $('#eventModal');
        el.find('.modal-title').text(ev.title);
        el.find('.event-date').text(Format.schedule(ev));
        el.find('.event-place').text(Format.placeLine(ev));
        MapView.toggleRow(el, '.event-contact', null);   // контакт се не приказује јавно
        MapView.toggleRow(el, '.event-organizer', ev.organizer);
        const note = Format.recurrenceNote(ev);
        const desc = el.find('.event-description').empty();
        if (note) {
            desc.append($('<div class="mb-2"></div>').append($('<span class="badge bg-secondary"></span>').text(note)));
        }
        if (ev.description) {
            desc.append($('<div style="white-space: pre-wrap;"></div>').text(ev.description));
        }
        desc.toggle(!!note || !!ev.description);
        el.find('.event-directions').attr('href', Format.directionsUrl(ev.latitude, ev.longitude));
        el.find('.event-link').attr('href', ev.link || '#').toggle(!!ev.link);
        MapView.modal.show();
    }

    /** Jedan odbor ili vise njih na istoj adresi. */
    public static showPoint(points: MapPoint | MapPoint[]): void {
        const list = Array.isArray(points) ? points : [points];
        const first = list[0];
        const el = $('#eventModal');
        el.find('.modal-title').text(list.length === 1 ? first.name : first.municipality);
        el.find('.event-date').text(list.length === 1 && !first.premises
                ? 'Одбор нема просторије – термини се одржавају на другим локацијама'
                : 'Нема унетих термина за изабрани дан');
        el.find('.event-place').text(`${first.municipality}, ${first.address}`);
        MapView.toggleRow(el, '.event-contact', null);
        MapView.toggleRow(el, '.event-organizer', null);

        const desc = el.find('.event-description').empty();
        if (list.length > 1) {
            desc.append($('<div class="fw-semibold mb-1"></div>').text('Одбори на овој адреси:'));
            list.forEach(p => {
                const row = $('<div class="mb-1"></div>');
                row.append($('<div></div>').text(p.name));
                row.append($('<div class="small text-muted"></div>')
                        .text(p.premises ? 'просторије одбора' : 'нема просторије'));
                desc.append(row);
            });
            desc.show();
        } else {
            desc.hide();
        }

        el.find('.event-directions').attr('href', Format.directionsUrl(first.latitude, first.longitude));
        el.find('.event-link').hide();
        MapView.modal.show();
    }

    private static toggleRow(el: JQuery, selector: string, value: string | null | undefined): void {
        el.find(selector).text(value || '');
        el.find(selector + '-row').toggle(!!value);
    }
}
